import { getLayoutForPage } from 'utils/pageLayoutUtils'
import { AppContext } from 'contexts/AppContext'
import { ViewerContext } from 'contexts/ViewerContext'
import SiteSEO from './SEO/SiteSEO'
import { ThemeProvider } from 'styled-components'
import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import SiteQuery from './api/queries/SiteQuery.gql'
import LOGGED_IN_QUERY from './api/queries/LoggedInQuery.gql'
import SET_USER_SITE_BUSINESS_MODEL from './api/mutations/setUserSiteBusinessModel.gql'
import SimpleErrorPage from './error/SimpleErrorPage'
import { setUser, trackExperiments } from 'utils/Analytics'
import { buildTheme, GlobalFont } from '@careguide/theme'
import { Toaster } from '@careguide/alert'
import ErrorPage from 'pages/_error'
import PeekabooProvider from './experiments/peekaboo/PeekabooProvider'
import { initSentry, setSentryUser } from 'utils/Sentry'
import Head from 'next/head'
import { removeKeyForUndefinedValues } from './experiments/peekaboo/experimentUtils'
import { CareGuidePage, PageProps } from 'types/careguide-page'
import { Site } from 'graph/Site'
import { LoggedInQuery } from 'graph/LoggedInQuery'
import {
  SetUserSiteBusinessModel,
  SetUserSiteBusinessModelVariables,
} from 'graph/SetUserSiteBusinessModel'
import { BusinessModelEnum } from 'graph/globalTypes'

type CareGuideCoreApplicationProps = {
  Component: CareGuidePage
  pageProps: PageProps
}

const CareGuideCoreApplication = (props: CareGuideCoreApplicationProps) => {
  const {
    Component,
    pageProps: { domain },
    pageProps,
  } = props

  const {
    loading: siteQueryLoading,
    error: siteQueryError,
    data: siteQueryData,
  } = useQuery<Site>(SiteQuery)

  const siteQueryHasUser = !siteQueryData || !!siteQueryData?.viewer?.user
  const { data: loggedInQueryData } = useQuery<LoggedInQuery>(LOGGED_IN_QUERY, {
    ssr: false,
    fetchPolicy: 'network-only',
    skip: siteQueryHasUser,
  })

  const [setUserSiteBusinessModel] = useMutation<
    SetUserSiteBusinessModel,
    SetUserSiteBusinessModelVariables
  >(SET_USER_SITE_BUSINESS_MODEL)

  const site = siteQueryData?.site
  const loggedInData = loggedInQueryData || siteQueryData
  const viewer = loggedInData?.viewer
  const user = viewer?.user
  const loggedIn = !!user
  const readyToReportUser = loggedIn || loggedInQueryData
  const { errorCode } = pageProps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => initSentry(user), [])

  useEffect(() => {
    if (readyToReportUser && user) {
      setUser(domain, user)
      setSentryUser(user)
    }
  }, [domain, readyToReportUser, user])

  const userAttributes = useMemo(() => {
    return removeKeyForUndefinedValues({
      firstStateForCurrentSite: user?.firstStateForCurrentSite,
    })
  }, [user?.firstStateForCurrentSite])

  if (siteQueryError) {
    return <SimpleErrorPage error={siteQueryError} />
  }

  if (siteQueryLoading || !site || !viewer) {
    return null
  }

  const Layout = getLayoutForPage(Component)
  const palette = site.palette
  const assets = site.assets

  const metaDescription = assets?.metaDescription
  const title = assets?.title || viewer.site?.pageTitle || ''
  const favicon = assets?.favicon

  // @ts-ignore the `buildTheme` types are wrong
  const theme = buildTheme(palette)

  const hasUpgradeCta = Component.hasUpgradeCta === true
  const hasFooter = Component.hideFooter !== true

  return (
    <AppContext.Provider value={{ domain, site }}>
      <Head>
        <title>{title}</title>
      </Head>
      <ViewerContext.Provider value={viewer}>
        <PeekabooProvider
          domain={domain}
          userId={user?.userId}
          userAttributes={userAttributes}
          onExperimentsLoaded={(experiments) => {
            trackExperiments(experiments)

            const businessModel = loggedInQueryData?.viewer?.site?.businessModel
            const inLimitedApplications = !!experiments.find(
              (experiment) => experiment.key === 'Limited Applications'
            )?.variation?.key

            if (inLimitedApplications && businessModel !== BusinessModelEnum.LIMITED_APPLICATIONS) {
              setUserSiteBusinessModel({
                variables: { businessModel: BusinessModelEnum.LIMITED_APPLICATIONS },
              })
            }
          }}
        >
          <SiteSEO faviconLink={favicon} metaTags={site.metaTags} />
          <ThemeProvider theme={theme}>
            <Toaster>
              <Layout
                hasFooter={hasFooter}
                hasUpgradeCta={hasUpgradeCta}
                viewer={viewer}
                loggedIn={loggedIn}
              >
                <GlobalFont />
                {errorCode ? (
                  <ErrorPage errorCode={errorCode} />
                ) : (
                  <Component
                    pageProps={pageProps}
                    metaDescription={metaDescription}
                    title={title}
                    loggedIn={loggedIn}
                    viewer={viewer}
                  />
                )}
              </Layout>
            </Toaster>
          </ThemeProvider>
        </PeekabooProvider>
      </ViewerContext.Provider>
    </AppContext.Provider>
  )
}

export default CareGuideCoreApplication
